import React, { useState, useEffect } from 'react';
import { Clock, TrendingUp, TrendingDown, Calendar, ChevronDown, ChevronUp, Info, Plus, X, CheckCircle } from 'lucide-react';
import { API_URL } from '../config';

export default function ModulationWidget({ authUser, authToken }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [modulationData, setModulationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [showRecuperationModal, setShowRecuperationModal] = useState(false);
  const [recuperationForm, setRecuperationForm] = useState({
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    title: 'Récupération',
    description: ''
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  // Charger les données de modulation du mois sélectionné
  useEffect(() => {
    if (!authUser || !authToken) return;
    loadModulationData();
  }, [selectedMonth, authUser, authToken]);

  const loadModulationData = async () => {
    setLoading(true);
    const [year, month] = selectedMonth.split('-');
    
    try {
      const response = await fetch(
        `${API_URL}/modulation/${authUser.id}?year=${year}&month=${month}`,
        {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setModulationData(data);
      }
    } catch (err) {
      console.error('Erreur chargement modulation:', err);
    }
    setLoading(false);
  };

  const formatHeures = (heures) => {
    const h = Math.floor(Math.abs(heures));
    const m = Math.round((Math.abs(heures) - h) * 60);
    const sign = heures >= 0 ? '+' : '-';
    return `${sign}${h}h${m.toString().padStart(2, '0')}`;
  };

  const getCompteurColor = (compteur) => {
    if (compteur > 0) return 'text-green-600 bg-green-50 dark:bg-green-950/40';
    if (compteur < 0) return 'text-red-600 bg-red-50 dark:bg-red-950/40';
    return 'text-text-secondary bg-elevated-1';
  };

  const getCompteurIcon = (compteur) => {
    if (compteur > 0) return <TrendingUp className="w-6 h-6" />;
    if (compteur < 0) return <TrendingDown className="w-6 h-6" />;
    return <Clock className="w-6 h-6" />;
  };

  const getCompteurMessage = (compteur) => {
    if (compteur > 0) return 'Heures à récupérer ou reporter';
    if (compteur < 0) return 'Heures à rattraper';
    return 'Vous êtes à jour';
  };

  const handleSubmitRecuperation = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/modulation/recuperation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employee_id: authUser.id,
          ...recuperationForm
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage({
          type: 'success',
          text: `✅ Récupération enregistrée ! ${data.heuresPosees?.toFixed(1)}h posées. Nouveau solde : ${data.nouveauCompteurEstime?.toFixed(1)}h`
        });
        
        // Recharger les données de modulation
        setTimeout(() => {
          loadModulationData();
          setShowRecuperationModal(false);
          setRecuperationForm({
            date: '',
            start_time: '09:00',
            end_time: '17:00',
            title: 'Récupération',
            description: ''
          });
          setSubmitMessage({ type: '', text: '' });
        }, 2000);
      } else {
        setSubmitMessage({
          type: 'error',
          text: data.message || data.error || 'Erreur lors de l\'enregistrement'
        });
      }
    } catch (err) {
      setSubmitMessage({
        type: 'error',
        text: 'Erreur de connexion au serveur'
      });
    }

    setSubmitLoading(false);
  };

  const calculateHeuresRecuperation = () => {
    if (!recuperationForm.start_time || !recuperationForm.end_time) return 0;
    const [h1, m1] = recuperationForm.start_time.split(':').map(Number);
    const [h2, m2] = recuperationForm.end_time.split(':').map(Number);
    return ((h2 * 60 + m2) - (h1 * 60 + m1)) / 60;
  };

  if (!authUser) return null;

  const heuresContrat = authUser.heures_hebdomadaires || 35;
  const compteurActuel = modulationData?.compteurActuel || 0;

  return (
    <div className="bg-surface rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Modulation</h2>
              <p className="text-indigo-100 text-sm">
                Contrat : {heuresContrat}h/sem ({modulationData?.heuresContratMensuel?.toFixed(2)}h/mois)
              </p>
            </div>
          </div>
          
          {/* Sélecteur de mois */}
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-4 py-2 rounded-lg text-text-primary font-semibold border-2 border-white/30 focus:outline-none focus:ring-2 focus:ring-white"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-500 dark:border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-text-secondary mt-3">Chargement...</p>
        </div>
      ) : (
        <>
          {/* Compteur cumulé - Grande carte centrale */}
          <div className="p-6 border-b-2 border-border-subtle">
            <div className={`${getCompteurColor(compteurActuel)} rounded-xl p-6 text-center transition`}>
              <div className="flex items-center justify-center gap-3 mb-2">
                {getCompteurIcon(compteurActuel)}
                <h3 className="text-lg font-semibold">Compteur cumulé</h3>
              </div>
              <div className="text-5xl font-bold mb-2">
                {formatHeures(compteurActuel)}
              </div>
              <p className="text-sm font-medium opacity-80">
                {getCompteurMessage(compteurActuel)}
              </p>
              
              {/* Bouton Poser une récupération - visible si compteur > 0 */}
              {compteurActuel >= 7 && (
                <button
                  onClick={() => setShowRecuperationModal(true)}
                  className="mt-4 px-6 py-3 bg-surface text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 dark:bg-indigo-950/40 transition flex items-center gap-2 mx-auto shadow-md"
                >
                  <Plus className="w-5 h-5" />
                  Poser une récupération
                </button>
              )}
              
              {compteurActuel > 0 && compteurActuel < 7 && (
                <p className="mt-3 text-xs opacity-70">
                  🔔 Vous pourrez poser une récupération à partir de 7h ({(7 - compteurActuel).toFixed(1)}h restantes)
                </p>
              )}
            </div>
          </div>

          {/* Détails du mois sélectionné */}
          {modulationData && !modulationData.error && (
            <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-sm text-text-secondary mb-1">Heures attendues</p>
                <p className="text-2xl font-bold text-text-primary">
                  {modulationData.heuresContratMensuel.toFixed(1)}h
                </p>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-text-secondary mb-1">Heures pointées</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {modulationData.heuresPointees.toFixed(1)}h
                </p>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-text-secondary mb-1">Récupération posée</p>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {modulationData.heuresRecuperation.toFixed(1)}h
                </p>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-text-secondary mb-1">Solde du mois</p>
                <p className={`text-2xl font-bold ${
                  modulationData.soldeMensuel >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {formatHeures(modulationData.soldeMensuel)}
                </p>
              </div>
            </div>
          )}

          {modulationData?.error && (
            <div className="p-6">
              <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-yellow-800">Configuration requise</p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    Votre contrat (heures hebdomadaires) n'est pas encore configuré. 
                    Contactez votre manager pour mettre à jour vos informations.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Historique (accordéon) */}
          <div className="border-t-2 border-border-subtle">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-elevated-1 transition"
            >
              <span className="font-semibold text-text-secondary">Historique des 12 derniers mois</span>
              {showHistory ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {showHistory && (
              <div className="p-6 bg-elevated-1">
                <p className="text-sm text-text-secondary italic">
                  Fonctionnalité à venir : historique détaillé mois par mois
                </p>
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Modal de récupération */}
      {showRecuperationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6" />
                <h3 className="text-xl font-bold">Poser une récupération</h3>
              </div>
              <button
                onClick={() => {
                  setShowRecuperationModal(false);
                  setSubmitMessage({ type: '', text: '' });
                }}
                className="hover:bg-white/20 rounded-full p-1 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmitRecuperation} className="p-6 space-y-4">
              {/* Solde disponible */}
              <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-sm text-green-700 font-medium">
                  💰 Solde disponible : <span className="text-2xl font-bold">{formatHeures(compteurActuel)}</span>
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  Environ {Math.floor(compteurActuel / 7)} jour{Math.floor(compteurActuel / 7) > 1 ? 's' : ''} de récupération
                </p>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  Date de récupération *
                </label>
                <input
                  type="date"
                  value={recuperationForm.date}
                  onChange={(e) => setRecuperationForm({ ...recuperationForm, date: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-border-strong rounded-lg focus:border-indigo-500 dark:border-indigo-500 focus:outline-none"
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Horaires */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Début *
                  </label>
                  <input
                    type="time"
                    value={recuperationForm.start_time}
                    onChange={(e) => setRecuperationForm({ ...recuperationForm, start_time: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-border-strong rounded-lg focus:border-indigo-500 dark:border-indigo-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Fin *
                  </label>
                  <input
                    type="time"
                    value={recuperationForm.end_time}
                    onChange={(e) => setRecuperationForm({ ...recuperationForm, end_time: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-border-strong rounded-lg focus:border-indigo-500 dark:border-indigo-500 focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Heures demandées */}
              <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  ⏱️ Heures demandées : <span className="font-bold">{calculateHeuresRecuperation().toFixed(1)}h</span>
                </p>
                {calculateHeuresRecuperation() > compteurActuel && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    ⚠️ Vous demandez plus d'heures que votre solde disponible !
                  </p>
                )}
              </div>

              {/* Titre */}
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  Titre (optionnel)
                </label>
                <input
                  type="text"
                  value={recuperationForm.title}
                  onChange={(e) => setRecuperationForm({ ...recuperationForm, title: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-border-strong rounded-lg focus:border-indigo-500 dark:border-indigo-500 focus:outline-none"
                  placeholder="Récupération"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  Description (optionnel)
                </label>
                <textarea
                  value={recuperationForm.description}
                  onChange={(e) => setRecuperationForm({ ...recuperationForm, description: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-border-strong rounded-lg focus:border-indigo-500 dark:border-indigo-500 focus:outline-none resize-none"
                  rows="3"
                  placeholder="Ex: Récupération des heures supplémentaires..."
                />
              </div>

              {/* Message de résultat */}
              {submitMessage.text && (
                <div className={`p-4 rounded-lg ${
                  submitMessage.type === 'success' 
                    ? 'bg-green-50 dark:bg-green-950/40 border border-green-200 text-green-700 dark:text-green-300' 
                    : 'bg-red-50 dark:bg-red-950/40 border border-red-200 text-red-700 dark:text-red-300'
                }`}>
                  <p className="text-sm font-medium">{submitMessage.text}</p>
                </div>
              )}

              {/* Boutons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRecuperationModal(false);
                    setSubmitMessage({ type: '', text: '' });
                  }}
                  className="flex-1 px-6 py-3 border-2 border-border-strong text-text-secondary rounded-lg font-semibold hover:bg-elevated-1 transition"
                  disabled={submitLoading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-700 transition disabled:opacity-50"
                  disabled={submitLoading || calculateHeuresRecuperation() > compteurActuel}
                >
                  {submitLoading ? 'Envoi...' : 'Valider'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
